import { useEffect, useState, useCallback, useRef } from 'react';
import { unByKey } from 'ol/Observable';

const defaultResolver = (zoom, view) => view?.get('layerName') ?? zoom;

const areExtentsEqual = (a, b, epsilon = 1) => {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((value, index) => Math.abs(value - b[index]) < epsilon);
};

const useViewportParams = ({ mapRef, isMapReady, resolveLayerName }) => {
    const [params, setParams] = useState(null);
    const lastParamsRef = useRef(null);

    const resolver = resolveLayerName ?? defaultResolver;

    const updateParams = useCallback(() => {
        const map = mapRef.current;
        if (!map) return;
        const view = map.getView();
        if (!view) return;

        const size = map.getSize();
        if (!size || !size[0] || !size[1]) return;

        const extent = view.calculateExtent(size);
        const zoom = view.getZoom();
        const layerName = resolver(zoom, view);

        const nextParams = {
            extents: extent,
            layerName,
            zoom,
        };

        const last = lastParamsRef.current;
        if (
            last &&
            areExtentsEqual(last.extents, nextParams.extents) &&
            last.layerName === nextParams.layerName &&
            last.zoom === nextParams.zoom
        ) {
            return;
        }

        lastParamsRef.current = nextParams;
        setParams(nextParams);
    }, [mapRef, resolver]);

    useEffect(() => {
        if (!isMapReady) return;
        const map = mapRef.current;
        if (!map) return;

        updateParams();
        const moveEndKey = map.on('moveend', updateParams);

        return () => {
            if (moveEndKey) {
                unByKey(moveEndKey);
            }
        };
    }, [isMapReady, mapRef, updateParams]);

    return params;
};

export default useViewportParams;
